import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material'
import {
  ArrowBack,
  Security,
  Person,
  Computer,
  Schedule,
  Edit,
  Save,
  Cancel,
  Info,
  DataObject,
  Comment,
  Add as AddIcon
} from '@mui/icons-material'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'

const AuditoriaDetailView = () => {
  const { auditId } = useParams()
  const navigate = useNavigate()
  const { user, isAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [auditDetail, setAuditDetail] = useState(null)
  const [notas, setNotas] = useState('')
  const [editingNotes, setEditingNotes] = useState(false)
  const [savingNotes, setSavingNotes] = useState(false)
  const [openJsonDialog, setOpenJsonDialog] = useState(false)
  const [jsonContent, setJsonContent] = useState('')
  const [jsonTitle, setJsonTitle] = useState('')

  useEffect(() => {
    loadAuditDetail()
  }, [auditId])

  const loadAuditDetail = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get(`/api/auditoria/${auditId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setAuditDetail(response.data)
      setNotas(response.data.notas || '')
    } catch (error) {
      setError('Error al cargar detalle de auditoría: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleSaveNotes = async () => {
    if (!notas.trim()) {
      setError('La nota no puede estar vacía')
      return
    }

    try {
      setSavingNotes(true)
      const token = localStorage.getItem('sgc_token')

      await axios.post(`/api/auditoria/${auditId}/notas`,
        { notas: notas.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      setEditingNotes(false)
      setError(null)
      // Recargar los detalles para mostrar la información actualizada
      await loadAuditDetail()
    } catch (error) {
      setError('Error al guardar nota: ' + (error.response?.data?.detail || error.message))
    } finally {
      setSavingNotes(false)
    }
  }

  const handleViewJson = (content, title) => {
    if (content && typeof content === 'object') {
      setJsonContent(JSON.stringify(content, null, 2))
    } else if (content && typeof content === 'string') {
      try {
        const parsed = JSON.parse(content)
        setJsonContent(JSON.stringify(parsed, null, 2))
      } catch {
        setJsonContent(content)
      }
    } else {
      setJsonContent('Sin datos')
    }
    setJsonTitle(title)
    setOpenJsonDialog(true)
  }

  const getAccionColor = (accion) => {
    switch (accion) {
      case 'CREATE': case 'INSERT': return 'success'
      case 'UPDATE': return 'warning'
      case 'DELETE': return 'error'
      case 'LOGIN': return 'info'
      case 'LOGOUT': return 'default'
      case 'LOGIN_FAILED': return 'error'
      case 'ACCESS': return 'primary'
      default: return 'default'
    }
  }

  const getAccionIcon = (accion) => {
    switch (accion) {
      case 'CREATE': case 'INSERT': return '+'
      case 'UPDATE': return '✏'
      case 'DELETE': return '🗑'
      case 'LOGIN': return '🔑'
      case 'LOGOUT': return '🚪'
      case 'LOGIN_FAILED': return '❌'
      case 'ACCESS': return '👁'
      default: return '📝'
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando detalle de auditoría...</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/auditoria')}
        >
          Volver a Auditoría
        </Button>
      </Box>
    )
  }

  if (!auditDetail) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Registro de auditoría no encontrado
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/auditoria')}
        >
          Volver a Auditoría
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Security sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              Detalle de Auditoría #{auditDetail.id}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip
                label={`${getAccionIcon(auditDetail.accion)} ${auditDetail.accion}`}
                color={getAccionColor(auditDetail.accion)}
                variant="outlined"
              />
              <Chip label={auditDetail.tabla} color="primary" size="small" />
              <Chip label={`ID: ${auditDetail.registro_id}`} color="default" size="small" />
            </Box>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/auditoria')}
        >
          Volver a Auditoría
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Información General */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Información General"
              avatar={<Info color="primary" />}
            />
            <CardContent>
              <List dense>
                <ListItem>
                  <ListItemIcon><Schedule /></ListItemIcon>
                  <ListItemText
                    primary="Fecha y Hora"
                    secondary={new Date(auditDetail.created_at).toLocaleString()}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText
                    primary="Usuario"
                    secondary={auditDetail.usuario?.nombre || 'Sistema'}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon><Computer /></ListItemIcon>
                  <ListItemText
                    primary="Dirección IP"
                    secondary={auditDetail.ip_address || 'No disponible'}
                  />
                </ListItem>

                {auditDetail.user_agent && (
                  <ListItem>
                    <ListItemText
                      primary="Navegador"
                      secondary={auditDetail.user_agent}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Datos del Cambio */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Datos del Cambio"
              avatar={<DataObject color="secondary" />}
            />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {auditDetail.datos_anteriores && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleViewJson(auditDetail.datos_anteriores, 'Datos Anteriores')}
                  >
                    Ver Datos Anteriores
                  </Button>
                )}

                {auditDetail.datos_nuevos && (
                  <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    onClick={() => handleViewJson(auditDetail.datos_nuevos, 'Datos Nuevos')}
                  >
                    Ver Datos Nuevos
                  </Button>
                )}

                {!auditDetail.datos_anteriores && !auditDetail.datos_nuevos && (
                  <Typography variant="body2" color="text.secondary">
                    No hay datos de cambio disponibles
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Notas */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Notas de Auditoría"
              avatar={<Comment color="info" />}
              action={
                (isAdmin || user?.rol === 'editor') && (
                  <Tooltip title={editingNotes ? "Cancelar edición" : "Editar notas"}>
                    <IconButton
                      onClick={() => {
                        if (editingNotes) {
                          setNotas(auditDetail.notas || '')
                          setEditingNotes(false)
                        } else {
                          setEditingNotes(true)
                        }
                      }}
                      disabled={savingNotes}
                    >
                      {editingNotes ? <Cancel /> : <Edit />}
                    </IconButton>
                  </Tooltip>
                )
              }
            />
            <CardContent>
              {editingNotes ? (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    placeholder="Agregar notas sobre este evento de auditoría..."
                    disabled={savingNotes}
                  />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={savingNotes ? <CircularProgress size={16} /> : <Save />}
                      onClick={handleSaveNotes}
                      disabled={savingNotes || !notas.trim()}
                    >
                      {savingNotes ? 'Guardando...' : 'Guardar Nota'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setNotas(auditDetail.notas || '')
                        setEditingNotes(false)
                      }}
                      disabled={savingNotes}
                    >
                      Cancelar
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  {auditDetail.notas ? (
                    <Box>
                      <Typography variant="body1" paragraph>
                        {auditDetail.notas}
                      </Typography>
                      {auditDetail.notas_usuario && auditDetail.notas_fecha && (
                        <Typography variant="caption" color="text.secondary">
                          Nota agregada por {auditDetail.notas_usuario.nombre} el{' '}
                          {new Date(auditDetail.notas_fecha).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        No hay notas para este evento
                      </Typography>
                      {(isAdmin || user?.rol === 'editor') && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          size="small"
                          onClick={() => setEditingNotes(true)}
                        >
                          Agregar Nota
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog para mostrar JSON */}
      <Dialog
        open={openJsonDialog}
        onClose={() => setOpenJsonDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{jsonTitle}</DialogTitle>
        <DialogContent>
          <Box component="pre" sx={{
            backgroundColor: 'grey.100',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            fontSize: '0.875rem',
            fontFamily: 'monospace'
          }}>
            {jsonContent}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenJsonDialog(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AuditoriaDetailView